import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, CircleDollarSign } from 'lucide-react';
import { useToast } from '../../hooks/usetoast';
import { useGetPrices, useEditPrices } from './api/index';

// Validation Schema
const schema = yup.object().shape({
    name: yup.string()
        .required('Currency name is required')
        .min(3, 'Name must be at least 3 characters')
        .max(10, 'Name must not exceed 10 characters'),
    symbol: yup.string()
        .required('Currency symbol is required'),
    image: yup.mixed()
        .nullable()
        .test('fileType', 'Only image files are allowed', (value) => {
            if (!value) return true; // Allow null (no new file uploaded)
            return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
        })
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value) return true; // Allow null
            return value.size <= 5 * 1024 * 1024;
        }),
    status: yup.boolean().required()
});

const PageLoader = () => (
    <div className="flex justify-center items-center h-64">
        <CircularProgress />
    </div>
);

function EditPricesCurrency() {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams();
    const { data: SinglePrice, isLoading } = useGetPrices(id);
    const updatePrice = useEditPrices();

    const { control, handleSubmit, formState: { errors, isValid, isDirty }, reset, setValue } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            name: '',
            symbol: '',
            image: null,
            status: true
        }
    });

    useEffect(() => {
        setValuesFromAPI();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SinglePrice]);

    const setValuesFromAPI = () => {
        if (SinglePrice?.data?.data?.length > 0) {
            const currency = SinglePrice.data.data.find(item => item._id === id);
            if (currency) {
                setValue('name', currency.name || '');
                setValue('symbol', currency.symbol || '');
                setValue('status', currency.status ?? true);
            } else {
                toast.error('Currency not found');
            }
        }
    };

    const onSubmit = async (data) => {
        const currency = SinglePrice?.data?.data?.find(item => item._id === id);
        if (!currency?._id) {
            toast.error('Missing Price ID');
            return;
        }

        if (!isDirty) {
            toast.info('No changes made to the form');
            return;
        }

        // Create FormData
        const formData = new FormData();
        formData.append('id', currency._id);
        formData.append('status', data?.data?.status.toString());
        formData.append('symbol', data?.data?.symbol);
        formData.append('name', data?.data?.name);
        if (data.image) {
            formData.append('image', data?.data?.image, data?.data?.image?.name);
        }

        try {
            const res = await updatePrice.mutateAsync(formData);
            if (res?.status) {
                toast.success('Price updated successfully');
                reset();
                navigate('/Chartcurrency');
            }
        } catch (error) {
            toast.error('Failed to update price');
        }
    };


    return (
        <div className="p-4 min-w-[40vw]">
            <div className="flex justify-between items-center mb-3">
                <h2 className="text-2xl font-bold">Edit Prices</h2>
                <ChevronLeft
                    size={18}
                    className="cursor-pointer"
                    onClick={() => navigate('/Chartcurrency')}
                />
            </div>
            <hr className="mb-4" />
            {isLoading ? (
                <PageLoader />
            ) : (
                <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                    <div className='mb-3'>
                        <h6 className="pb-2">Currency Name</h6>
                        <div className="input-group">
                            <button type="button" className="btn p-0 input-group-icon border-0">
                                <CircleDollarSign size={20} color='#aaa9a9' />
                            </button>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter currency name"
                                    />
                                )}
                            />
                        </div>
                        {errors.name && <p className="errMsg text-red-500 text-sm mt-1">{errors.name.message}</p>}
                    </div>

                    <div className='mb-3'>
                        <h6 className="pb-2">Currency Symbol</h6>
                        <div className="input-group">
                            <button type="button" className="btn p-0 input-group-icon border-0">
                                <CircleDollarSign size={20} color='#aaa9a9' />
                            </button>
                            <Controller
                                name="symbol"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter currency symbol"
                                    />
                                )}
                            />
                        </div>
                        {errors.symbol && <p className="errMsg text-red-500 text-sm mt-1">{errors.symbol.message}</p>}
                    </div>

                    <div className="mb-4">
                        <h6 className="pb-2 font-semibold">Image</h6>
                        <Controller
                            name="image"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        onChange(file);
                                    }}
                                    className="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                                />
                            )}
                        />
                        {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image.message}</p>}
                        {SinglePrice?.data?.data?.length > 0 && (
                            (() => {
                                const currency = SinglePrice.data.data.find(item => item._id === id);
                                return currency?.image ? (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-600">Current image:</p>
                                        <img
                                            src={currency.image}
                                            alt="Current currency"
                                            className="mt-1 w-24 h-24 object-contain"
                                        />
                                    </div>
                                ) : null;
                            })()
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="flex items-center space-x-2">
                            <Controller
                                name="status"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <input
                                        type="checkbox"
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                        className="form-checkbox"
                                    />
                                )}
                            />
                            <span>Active Status</span>
                        </label>
                    </div>

                    <div className="flex justify-center">
                        <button
                            type="submit"
                            className="bg-blue-500 text-white px-6 py-2 rounded-full hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                            disabled={updatePrice.isLoading || !isValid}
                        >
                            {updatePrice.isLoading ? (
                                <CircularProgress size={20} className="text-white mx-4" />
                            ) : (
                                "Submit"
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

export default EditPricesCurrency;